import { useMemo, useState } from 'react';
import { Library, FileVideo, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useAssets, type Asset } from '@/api/fastchannel';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import { useDebounce } from '../shared';
import { formatDurationShort, isAssetReady } from './shared';
import { AssetStatusPill } from './AssetStatusPill';
import styles from './Fastchannel.module.css';

interface LibraryPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  /** Selecting an asset hands back its built `url` + resolved `duration_seconds`,
   *  so the caller can set the source without a separate duration probe. */
  onSelect: (asset: Asset) => void;
}

/** Pick an existing Content Library asset as a program source. */
export function LibraryPickerDialog({ open, onOpenChange, serviceUuid, onSelect }: LibraryPickerDialogProps) {
  const { data: assets = [], isLoading } = useAssets(serviceUuid);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 200).trim().toLowerCase();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = useMemo<Asset[]>(() => {
    if (!search) return assets;
    return assets.filter(
      (a) => a.name?.toLowerCase().includes(search) || a.object_key?.toLowerCase().includes(search),
    );
  }, [assets, search]);

  const selected = assets.find((a) => a.id === selectedId) ?? null;

  const confirm = () => {
    // Only a fully-normalized asset has a playable url; never hand back a
    // pending/normalizing/failed one (its url is empty → broken schedule).
    if (selected && isAssetReady(selected.status)) onSelect(selected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pick from library</DialogTitle>
          <DialogDescription>
            Choose a media asset. Only fully-processed (Ready) assets can be scheduled.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Skeleton style={{ height: 320 }} />
          ) : assets.length === 0 ? (
            <EmptyState
              icon={<Library size={20} />}
              title="No assets yet"
              description="Upload or add assets in the Content Library first."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Input
                placeholder="Find assets by name or key"
                startSlot={<Search />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {rows.length === 0 ? (
                <EmptyState icon={<Library size={20} />} title="No assets match your search." description="Try a different search term." />
              ) : (
                <div className={styles.fileList}>
                  {rows.map((a) => {
                    const ready = isAssetReady(a.status);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={!ready}
                        title={ready ? undefined : 'Asset is still being processed'}
                        className={`${styles.fileRow}${selectedId === a.id ? ` ${styles.fileRowActive}` : ''}`}
                        style={ready ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                        onClick={() => ready && setSelectedId(a.id)}
                      >
                        <FileVideo size={16} />
                        <span className={styles.fileName}>{a.name || a.object_key}</span>
                        {ready ? (
                          <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
                            {a.duration_seconds > 0 ? formatDurationShort(a.duration_seconds) : '—'}
                            {a.size_bytes > 0 ? ` · ${formatBytes(a.size_bytes)}` : ''}
                          </span>
                        ) : (
                          <AssetStatusPill status={a.status} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selected} onClick={confirm}>
            Select asset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
